// Re-plan on the task page (spec J3, P5-R1): who works the task and its dates. The buttons follow
// the permissions the API sends with the page; the API checks again on every save.
import { useState } from 'react';
import type { TaskDetail } from '@cis/api-client';
import { AssignDialog } from './assign-dialog.tsx';
import { DatesDialog } from './dates-dialog.tsx';
import { formatDate } from './format.ts';
import type { TaskWriter } from './use-task-writer.ts';

type Open = 'assign' | 'dates' | null;

function PlannedDates(props: { readonly detail: TaskDetail }): React.JSX.Element {
  const { detail } = props;
  const { replannedStart, replannedEnd } = detail.state;
  const replanned = replannedStart !== null || replannedEnd !== null;
  return (
    <dl className="facts">
      <dt>Planned in the pack</dt>
      <dd>
        {formatDate(detail.plannedStart)} to {formatDate(detail.plannedEnd)}
      </dd>
      {replanned && (
        <>
          <dt>Re-planned</dt>
          <dd>
            {formatDate(replannedStart ?? detail.plannedStart)} to{' '}
            {formatDate(replannedEnd ?? detail.plannedEnd)}
          </dd>
        </>
      )}
    </dl>
  );
}

/** "Plan and people": the dates, plus Assign people and Change dates for those allowed. */
export function PlanPanel(props: {
  readonly detail: TaskDetail;
  readonly writer: TaskWriter;
}): React.JSX.Element | null {
  const { detail, writer } = props;
  const [open, setOpen] = useState<Open>(null);
  const { assign, replan } = detail.permissions;
  if (!assign && !replan) return null;
  const close = (): void => setOpen(null);
  return (
    <section className="card" aria-labelledby="plan-title">
      <h2 className="card__title" id="plan-title">
        Plan and people
      </h2>
      <PlannedDates detail={detail} />
      <div className="form-row" role="group" aria-label="Re-plan">
        {assign && (
          <button
            type="button"
            className="btn btn--secondary"
            disabled={writer.pending}
            onClick={() => setOpen('assign')}
          >
            Assign people
          </button>
        )}
        {replan && (
          <button
            type="button"
            className="btn btn--secondary"
            disabled={writer.pending}
            onClick={() => setOpen('dates')}
          >
            Change dates
          </button>
        )}
      </div>
      {open === 'assign' && (
        <AssignDialog
          title={`Assign people to ${detail.taskId}`}
          detail={detail}
          assigned={(d) => d.assignees}
          writer={writer}
          toWrite={(userIds) => ({ kind: 'assign', userIds })}
          onClose={close}
        />
      )}
      {open === 'dates' && <DatesDialog detail={detail} writer={writer} onClose={close} />}
    </section>
  );
}
